import { useState, useRef, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError, limitWords, countWords } from "@/lib/format";
import { RemarkableMindsButton } from "@/components/GeniusAvenue";
import { PileButton } from "@/components/PileModal";
import { useAuth } from "@/context/AuthContext";
import { DnaHelix } from "@/components/DnaHelix";
import { MyAsks } from "@/components/MyAsks";
import { toast } from "sonner";

function ConfirmModal({ text, busy, onCancel, onConfirm }) {
  const [subject, setSubject] = useState("");
  const canSubmit = subject.trim().length > 0 && !busy;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 fade-in"
      style={{ background: "rgba(0,0,0,0.7)" }}
      onClick={onCancel}
      data-testid="ask-confirm-overlay"
    >
      <div
        className="w-full max-w-xl p-8"
        style={{ background: "var(--bg-modal)", border: "1px solid var(--border)" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="ask-confirm-modal"
      >
        <span className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
          Your idea
        </span>
        <p className="long-text font-head font-light text-xl mt-3 mb-8 leading-relaxed" style={{ color: "var(--text-primary)" }}>
          {text}
        </p>

        <label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Subject</label>
        <input
          data-testid="ask-subject-input"
          value={subject}
          onChange={(e) => setSubject(limitWords(e.target.value, subject, 5))}
          onKeyDown={(e) => { if (e.key === "Enter" && canSubmit) onConfirm(subject.trim()); }}
          autoFocus
          placeholder=""
          className="w-full py-3 mt-2 text-lg"
          style={{ background: "transparent", borderBottom: "1px solid var(--border)", color: "var(--text-primary)" }}
        />

        <div className="flex gap-4 mt-10">
          <button
            onClick={onCancel}
            data-testid="ask-cancel-btn"
            className="btn flex-1 py-3 text-sm tracking-widest"
          >
            CANCEL
          </button>
          <button
            onClick={() => canSubmit && onConfirm(subject.trim())}
            disabled={!canSubmit}
            data-testid="ask-confirm-btn"
            className="btn flex-1 py-3 text-sm tracking-widest"
          >
            {busy ? "…" : "SUBMIT"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AskPage() {
  const { user } = useAuth();
  const isGuest = user?.is_guest;
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [focused, setFocused] = useState(false);
  const [quota, setQuota] = useState(null);
  const [remaining, setRemaining] = useState("");
  const [submittedAt, setSubmittedAt] = useState(0);
  const ref = useRef(null);

  const count = quota?.count ?? 0;
  const limit = quota?.limit ?? 13;
  const flipped = count === 7;
  const gameOver = count >= limit;

  const loadQuota = useCallback(async () => {
    try {
      const { data } = await api.get("/inquiries/quota");
      setQuota(data);
    } catch (err) {
      console.error("Failed to load prompt quota", err);
    }
  }, []);

  useEffect(() => { loadQuota(); }, [loadQuota]);

  useEffect(() => {
    if (!gameOver || !quota?.reset_at) { setRemaining(""); return; }
    const target = new Date(quota.reset_at).getTime();
    let done = false;
    const tick = () => {
      const diff = target - Date.now();
      if (diff <= 0) {
        setRemaining("00:00:00");
        if (!done) { done = true; loadQuota(); }
        return;
      }
      const h = String(Math.floor(diff / 3600000)).padStart(2, "0");
      const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0");
      const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");
      setRemaining(`${h}:${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [gameOver, quota?.reset_at, loadQuota]);

  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = "auto";
      ref.current.style.height = ref.current.scrollHeight + "px";
    }
  }, [text]);

  const attempt = () => {
    if (!text.trim() || busy) return;
    if (gameOver) { toast("Game over — fresh asks unlock when the clock runs out"); return; }
    setConfirming(true);
  };

  const confirmSubmit = async (subject) => {
    if (isGuest) {
      toast("Sign in");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/inquiries", { text, subject });
      setText("");
      setConfirming(false);
      setSubmittedAt(Date.now());
      if (res.data?.quota) setQuota(res.data.quota);
      toast.success("Ask submitted");
    } catch (err) {
      if (err.response?.status === 429) loadQuota();
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      attempt();
    }
  };

  return (
    <div className="flex flex-col items-center" style={{ marginTop: "12vh" }}>
      <div className="fixed left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-3" style={{ top: "125px" }}>
        <PileButton />
        <RemarkableMindsButton />
      </div>
      <div
        data-testid="prompt-quota"
        className="mb-10 text-2xl font-head font-light tracking-widest transition-transform duration-500"
        style={{ color: "var(--text-primary)", transform: flipped ? "rotate(180deg)" : "none" }}
      >
        {count}/{limit}
      </div>
      <div className="relative w-full max-w-3xl">
        <textarea
          ref={ref}
          data-testid="ask-input"
          value={text}
          onChange={(e) => setText(limitWords(e.target.value, text, 1000))}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          rows={1}
          placeholder=""
          disabled={gameOver}
          className="w-full text-center text-2xl md:text-3xl font-head font-light resize-none overflow-hidden"
          style={{
            background: "transparent",
            color: "var(--text-primary)",
            lineHeight: 1.5,
            pointerEvents: gameOver ? "none" : undefined,
          }}
        />
        {!focused && !text.trim() && (
          <div
            className="absolute inset-0 flex items-center justify-center fade-in"
            style={{ pointerEvents: "none" }}
            data-testid="ask-helix-overlay"
          >
            <DnaHelix />
          </div>
        )}
      </div>
      <div className="mt-4 h-4 text-xs tracking-widest" data-testid="ask-word-count" style={{ color: countWords(text) >= 1000 ? "var(--accent)" : "var(--text-muted)" }}>
        {text.trim() && `${countWords(text).toLocaleString()} / 1,000 words`}
      </div>
      <div className="mt-12 min-h-[1.5rem] w-full max-w-3xl flex items-center justify-between gap-6">
        <span />
        {text.trim() && (
          <button
            onClick={attempt}
            disabled={busy}
            data-testid="ask-submit-btn"
            className="text-xs tracking-widest fade-in transition-colors duration-200"
            style={{ color: "var(--accent)" }}
          >
            {busy ? "…" : "press enter to submit ↵"}
          </button>
        )}
      </div>

      {confirming && (
        <ConfirmModal
          text={text}
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={confirmSubmit}
        />
      )}

      {gameOver && (
        <div className="fixed bottom-72 left-1/2 -translate-x-1/2 text-center fade-in" data-testid="game-over">
          <div className="text-lg tracking-widest uppercase" style={{ color: "var(--accent)" }} data-testid="game-over-text">
            game over
          </div>
          {remaining && (
            <div className="text-sm tracking-widest mt-3" style={{ color: "var(--text-muted)" }} data-testid="game-over-clock">
              {remaining}
            </div>
          )}
        </div>
      )}

      <MyAsks refreshKey={submittedAt} />
    </div>
  );
}
